import { useState, useEffect } from 'react'
import { useRoomContext } from '@livekit/components-react'

export interface WebRTCStats {
  // Connection
  transport: string
  localIp: string
  remoteIp: string
  rtt: number

  // Audio send
  audioSendCodec: string
  audioSendBitrate: number
  audioSendPacketLoss: number
  audioSendJitter: number

  // Audio recv
  audioRecvCodec: string
  audioRecvBitrate: number
  audioRecvPacketLoss: number
  audioRecvJitter: number

  // Video send
  videoSendCodec: string
  videoSendBitrate: number
  videoSendWidth: number
  videoSendHeight: number
  videoSendFps: number
  videoSendPacketLoss: number

  // Video recv
  videoRecvCodec: string
  videoRecvBitrate: number
  videoRecvWidth: number
  videoRecvHeight: number
  videoRecvFps: number
  videoRecvPacketLoss: number
}

const EMPTY: WebRTCStats = {
  transport: '-', localIp: '-', remoteIp: '-', rtt: 0,
  audioSendCodec: '-', audioSendBitrate: 0, audioSendPacketLoss: 0, audioSendJitter: 0,
  audioRecvCodec: '-', audioRecvBitrate: 0, audioRecvPacketLoss: 0, audioRecvJitter: 0,
  videoSendCodec: '-', videoSendBitrate: 0, videoSendWidth: 0, videoSendHeight: 0, videoSendFps: 0, videoSendPacketLoss: 0,
  videoRecvCodec: '-', videoRecvBitrate: 0, videoRecvWidth: 0, videoRecvHeight: 0, videoRecvFps: 0, videoRecvPacketLoss: 0,
}

export function useWebRTCStats(enabled: boolean) {
  const room = useRoomContext()
  const [stats, setStats] = useState<WebRTCStats>(EMPTY)

  useEffect(() => {
    if (!enabled || !room) { setStats(EMPTY); return }

    let prevBytesSent = 0, prevBytesRecv = 0
    let prevAudioBytesSent = 0, prevAudioBytesRecv = 0
    let prevTimestamp = 0

    const parseReport = (report: RTCStatsReport, s: Partial<WebRTCStats>, direction: 'send' | 'recv' | 'both') => {
      let vBytesSent = 0, vBytesRecv = 0, aBytesSent = 0, aBytesRecv = 0, ts = 0
      // Track the highest-resolution outbound-rtp for video (main track, not simulcast layers)
      let bestVideoSendBytes = 0

      report.forEach((r: any) => {
        if (r.type === 'candidate-pair' && r.state === 'succeeded') {
          s.rtt = Math.round((r.currentRoundTripTime || 0) * 1000)
          s.transport = r.protocol || 'udp'
        }
        if (r.type === 'local-candidate' && r.candidateType) {
          s.localIp = `${r.address || ''}:${r.port || ''}`
        }
        if (r.type === 'remote-candidate' && r.candidateType) {
          s.remoteIp = `${r.address || ''}:${r.port || ''}`
        }
        if (r.type === 'outbound-rtp' && (direction === 'send' || direction === 'both')) {
          ts = r.timestamp
          if (r.kind === 'audio') {
            aBytesSent += (r.bytesSent || 0)
            s.audioSendCodec = r.mimeType?.split('/')[1] || s.audioSendCodec || '-'
          }
          if (r.kind === 'video') {
            vBytesSent += (r.bytesSent || 0)
            // Pick the highest-res encoding for display stats
            const w = r.frameWidth || 0
            if (w >= (s.videoSendWidth || 0)) {
              s.videoSendCodec = r.mimeType?.split('/')[1] || s.videoSendCodec || '-'
              s.videoSendWidth = w
              s.videoSendHeight = r.frameHeight || 0
              s.videoSendFps = Math.round(r.framesPerSecond || 0)
            }
            s.videoSendPacketLoss = r.packetsLost ? Math.round(r.packetsLost / (r.packetsSent || 1) * 100) : 0
          }
        }
        if (r.type === 'inbound-rtp' && (direction === 'recv' || direction === 'both')) {
          ts = r.timestamp
          if (r.kind === 'audio') {
            aBytesRecv += (r.bytesReceived || 0)
            s.audioRecvCodec = r.mimeType?.split('/')[1] || s.audioRecvCodec || '-'
            s.audioRecvJitter = Math.round((r.jitter || 0) * 1000)
            s.audioRecvPacketLoss = r.packetsLost ? Math.round(r.packetsLost / ((r.packetsReceived || 0) + (r.packetsLost || 0)) * 100) : 0
          }
          if (r.kind === 'video') {
            vBytesRecv += (r.bytesReceived || 0)
            s.videoRecvCodec = r.mimeType?.split('/')[1] || s.videoRecvCodec || '-'
            s.videoRecvWidth = r.frameWidth || 0
            s.videoRecvHeight = r.frameHeight || 0
            s.videoRecvFps = Math.round(r.framesPerSecond || 0)
            s.videoRecvPacketLoss = r.packetsLost ? Math.round(r.packetsLost / ((r.packetsReceived || 0) + (r.packetsLost || 0)) * 100) : 0
          }
        }
      })

      return { vBytesSent, vBytesRecv, aBytesSent, aBytesRecv, ts }
    }

    const poll = async () => {
      try {
        const engine = (room as any).engine
        const pubPc: RTCPeerConnection | null = engine?.publisher?.pc || null
        const subPc: RTCPeerConnection | null = engine?.subscriber?.pc || null
        if (!pubPc && !subPc) return

        const s: Partial<WebRTCStats> = {}
        let totalVSent = 0, totalVRecv = 0, totalASent = 0, totalARecv = 0, timestamp = 0

        // Publisher PC → send stats + connection info
        if (pubPc) {
          const report = await pubPc.getStats()
          const r = parseReport(report, s, 'send')
          totalVSent = r.vBytesSent; totalASent = r.aBytesSent
          timestamp = r.ts || timestamp
        }

        // Subscriber PC → recv stats (+ connection fallback)
        if (subPc) {
          const report = await subPc.getStats()
          const r = parseReport(report, s, 'recv')
          totalVRecv = r.vBytesRecv; totalARecv = r.aBytesRecv
          if (!timestamp) timestamp = r.ts
          // Connection info fallback from subscriber if publisher had none
          if (s.transport === '-' || !s.transport) {
            const sf: Partial<WebRTCStats> = {}
            parseReport(report, sf, 'both')
            if (sf.transport && sf.transport !== '-') s.transport = sf.transport
            if (sf.localIp && sf.localIp !== '-') s.localIp = sf.localIp
            if (sf.remoteIp && sf.remoteIp !== '-') s.remoteIp = sf.remoteIp
            if (sf.rtt) s.rtt = sf.rtt
          }
        }

        if (prevTimestamp > 0 && timestamp > prevTimestamp) {
          const dt = (timestamp - prevTimestamp) / 1000
          s.videoSendBitrate = Math.round((totalVSent - prevBytesSent) * 8 / dt)
          s.videoRecvBitrate = Math.round((totalVRecv - prevBytesRecv) * 8 / dt)
          s.audioSendBitrate = Math.round((totalASent - prevAudioBytesSent) * 8 / dt)
          s.audioRecvBitrate = Math.round((totalARecv - prevAudioBytesRecv) * 8 / dt)
        }

        prevBytesSent = totalVSent
        prevBytesRecv = totalVRecv
        prevAudioBytesSent = totalASent
        prevAudioBytesRecv = totalARecv
        prevTimestamp = timestamp

        setStats(prev => ({ ...prev, ...s }))
      } catch (e) {
        console.warn('[webrtc-stats] poll error:', e)
      }
    }

    const id = setInterval(poll, 2000)
    poll()
    return () => clearInterval(id)
  }, [enabled, room])

  return stats
}
